/**
 * RegisterPage — Registo de novo utilizador com indicador de força de palavra-passe.
 */
import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { getApiErrorMessage } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

function passwordStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const labels = ["", "Muito fraca", "Fraca", "Razoável", "Boa", "Forte"];
    const colors = ["", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-blue-500", "bg-green-500"];
    return { score, label: labels[score] || "", color: colors[score] || "" };
}

export default function RegisterPage() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        country: "Angola",
        password: "",
    });
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    const strength = useMemo(() => passwordStrength(form.password), [form.password]);
    const passwordsMatch = !confirmPassword || form.password === confirmPassword;

    const submit = async (e) => {
        e.preventDefault();
        if (form.password !== confirmPassword) {
            toast.error("As palavras-passe não coincidem.");
            return;
        }
        if (strength.score < 4) {
            toast.error("A palavra-passe é demasiado fraca. Use pelo menos 12 caracteres com maiúscula, minúscula, número e símbolo.");
            return;
        }
        setLoading(true);
        try {
            const u = await register(form);
            toast.success(`Conta criada! Bem-vindo, ${u.name}.`);
            nav("/painel");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Não foi possível registar."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center py-10 px-4 bg-hero-radial-light" data-testid="register-page">
            <Card className="w-full max-w-md p-7 rounded-2xl bg-card border-border">
                <div className="flex justify-center mb-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B0B10] overflow-hidden">
                        <img src={LOGO_URL} alt="MeSafa" className="h-14 w-14 object-contain" />
                    </span>
                </div>
                <h1 className="font-display text-2xl font-semibold text-center">Criar conta</h1>
                <p className="text-sm text-muted-foreground text-center mt-1">7 dias grátis. Sem cartão.</p>

                <form onSubmit={submit} className="mt-6 space-y-3">
                    <div>
                        <Label htmlFor="r-name">Nome completo *</Label>
                        <Input id="r-name" required minLength={2} maxLength={120} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name-input" />
                    </div>
                    <div>
                        <Label htmlFor="r-email">Email *</Label>
                        <Input id="r-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email-input" />
                    </div>
                    <div>
                        <Label htmlFor="r-company">Empresa</Label>
                        <Input id="r-company" maxLength={160} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="register-company-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="r-phone">Telefone</Label>
                            <Input id="r-phone" maxLength={32} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="register-phone-input" />
                        </div>
                        <div>
                            <Label htmlFor="r-country">País</Label>
                            <Input id="r-country" maxLength={80} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="register-country-input" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="r-password">Palavra-passe *</Label>
                        <div className="relative">
                            <Input
                                id="r-password"
                                type={showPwd ? "text" : "password"}
                                required
                                minLength={12}
                                maxLength={128}
                                autoComplete="new-password"
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                data-testid="register-password-input"
                                className="pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                tabIndex={-1}
                            >
                                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        {form.password && (
                            <div className="mt-1.5 space-y-1">
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div key={i} className={"h-1 flex-1 rounded-full transition-all " + (i <= strength.score ? strength.color : "bg-muted")} />
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {strength.label && <span className="font-medium">{strength.label}</span>}
                                    {strength.score < 5 && " — use maiúscula, minúscula, número e símbolo (mín. 12 caracteres)"}
                                </p>
                            </div>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="r-confirm">Confirmar palavra-passe *</Label>
                        <div className="relative">
                            <Input
                                id="r-confirm"
                                type={showConfirm ? "text" : "password"}
                                required
                                minLength={12}
                                maxLength={128}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                data-testid="register-confirm-password-input"
                                className={"pr-10" + (!passwordsMatch ? " border-destructive" : "")}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                tabIndex={-1}
                            >
                                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        {!passwordsMatch && (
                            <p className="text-xs text-destructive mt-1">As palavras-passe não coincidem.</p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        disabled={loading || !passwordsMatch}
                        className="w-full btn-grad-purple text-white h-11"
                        data-testid="register-submit-button"
                    >
                        {loading ? "A criar conta…" : "Criar conta grátis"}
                    </Button>
                </form>

                <p className="mt-5 text-center text-sm text-muted-foreground">
                    Já tem conta?{" "}
                    <Link to="/entrar" className="text-[#6B21D9] font-medium hover:underline">Entrar</Link>
                </p>
            </Card>
        </div>
    );
}
